// @ts-nocheck
// Ported byte-identically from v1
// components/partials/components/AgentNotificationPopup.js.
// Popup panel that anchors underneath the header notification bell:
//   - Optimistic mark-read: local state flips before the API call so the
//     UI is snappy even on flaky links.
//   - Click-outside dismiss (mousedown listener installed while `show`).
//   - Renders one card per unread/read notification, sorted by caller order
//     (server-side).
import { useEffect, useRef } from 'react';
import { postApiCall } from '@/service/httpCall';
import {
  API_NOTIFICATION_MARK_READ,
  API_NOTIFICATION_MARK_ALL_READ,
} from '@/service/url';
import { Loader } from 'lucide-react';

interface Props {
  show: boolean;
  onHide: () => void;
  setHasUnread?: (v: boolean) => void;
  notifications: any[];
  setNotifications: (updater: any) => void;
  isLoading?: boolean;
}

export default function AgentNotificationPopup({
  show,
  onHide,
  setHasUnread,
  notifications,
  setNotifications,
  isLoading = false,
}: Props) {
  const popupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onHide();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onHide]);

  const markAsRead = async (id: any) => {
    setNotifications((prev: any[]) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    try {
      await postApiCall(API_NOTIFICATION_MARK_READ, {
        notification_ids: [String(id)],
      });
    } catch (err) {
      console.error('Failed to mark notification as read', err);
    }
  };

  const markAllRead = async () => {
    setNotifications((prev: any[]) => prev.map((n) => ({ ...n, is_read: true })));
    if (typeof setHasUnread === 'function') setHasUnread(false);
    try {
      await postApiCall(API_NOTIFICATION_MARK_ALL_READ, {});
    } catch (err) {
      console.error('Failed to mark all notifications as read', err);
    }
  };

  const formatDate = (dateStr: any) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const visibleNotifications = (notifications || []).filter((n) => !n.is_deleted);

  if (!show) return null;

  return (
    <div
      ref={popupRef}
      // Below md the bell sits near the left edge, so anchoring to it pushes
      // the popup off-screen; pin it to the viewport's right edge instead.
      className="max-md:!fixed max-md:!right-3 max-md:!top-[60px]"
      style={{
        position: 'absolute',
        top: '30px',
        right: '0',
        zIndex: 1050,
        width: 'min(320px, calc(100vw - 24px))',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '10px',
        maxHeight: '400px',
        overflowY: 'auto',
        color: '#333',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #ddd',
          paddingBottom: '10px',
          marginBottom: '10px',
        }}
      >
        <div style={{ fontWeight: 'bold' }}>Notifications</div>
        <button
          type="button"
          title="Mark all as read"
          onClick={markAllRead}
          style={{
            border: '1px solid #00b10f',
            background: 'transparent',
            color: '#00b10f',
            fontSize: '11px',
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Mark all as read
        </button>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <Loader className="animate-spin" />
        </div>
      ) : visibleNotifications.length === 0 ? (
        <p style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
          No notifications
        </p>
      ) : (
        visibleNotifications.map((n) => (
          <div
            key={n.id}
            style={{
              padding: '10px',
              marginBottom: '10px',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              border: n.is_read ? '1px solid #00b10f' : '1px solid red',
            }}
          >
            <div
              onClick={() => {
                if (!n.is_read) markAsRead(n.id);
              }}
              style={{
                cursor: n.is_read ? 'default' : 'pointer',
                flex: 1,
                minWidth: 0,
                marginRight: '10px',
              }}
            >
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  marginBottom: '4px',
                }}
              >
                {n.notification_type}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  lineHeight: '1.4',
                }}
              >
                {n.description}
                <br />
                <small style={{ color: '#999', display: 'block', marginTop: '5px' }}>
                  {formatDate(n.created_at)}
                </small>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {!n.is_read && (
                <button
                  type="button"
                  title="Mark as read"
                  onClick={(e) => {
                    e.stopPropagation();
                    markAsRead(n.id);
                  }}
                  style={{
                    border: '1px solid #00b10f',
                    background: 'transparent',
                    color: '#00b10f',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Mark as read
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
